import React, { useCallback, useMemo } from 'react';
import { FAQItem, OfferList, CreditsInfosSection, Button } from 'components';
import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Offer, OfferType, useOffers } from '@app/api';
import { useNavigate } from 'react-router-dom';
import { G_CALENDAR_LINK } from 'components/GoogleCalendar/gCalendar';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  container: {
    marginInline: '8vw',
    padding: [[theme.marginBase * 7, theme.marginBase * 12]],
    minHeight: '100vh',
    [theme.media.mobile]: {
      padding: [[theme.marginBase * 8, theme.marginBase * 2]],
      minHeight: 'auto',
    },

    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.marginBase * 5 + 'px',
  },
  h1: {
    ...theme.fonts.h2,
    margin: theme.marginBase * 3,
    maxWidth: theme.marginBase * 60,
    textAlign: 'center',
  },
  bannerContainer: {
    ...theme.fonts.h5,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.marginBase * 3,
    padding: theme.marginBase * 3,
    backgroundColor: theme.colors.greenMuted,
    borderRadius: theme.borderRadius.std,
  },
  faqContainer: {
    backgroundColor: theme.colors.white,
    padding: theme.marginBase * 3,
    borderRadius: theme.borderRadius.std,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  faqTitle: {
    ...theme.fonts.h4,
    color: theme.colors.inkPrimary,
  },
}));

export const EmptyCreditsPage = () => {
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const navigate = useNavigate();

  const { data: offers } = useOffers();
  const creditOffers = useMemo(() => {
    if (!offers) return [];
    return offers
      .filter((offer) => offer.type === OfferType.CREDIT)
      .sort((a, b) => {
        if (a.prices.eur === 0) return 1;
        if (b.prices.eur === 0) return -1;
        return a.prices.eur - b.prices.eur;
      });
  }, [offers]);

  const onOfferClick = useCallback(
    (offer: Offer) => navigate(`/payment?offer=${offer.id}`),
    [navigate],
  );

  return (
    <div className={classes.container}>
      <h1 className={classes.h1}>{t('billing.emptyPage.title')}</h1>
      <CreditsInfosSection />
      <OfferList offers={creditOffers} onClick={onOfferClick} />

      <div className={classes.bannerContainer}>
        {t('billing.emptyPage.needHelpBanner.text')}
        <Button
          href={G_CALENDAR_LINK}
          target="_blank"
          text={t('billing.emptyPage.needHelpBanner.button')}
        />
      </div>
      <div className={classes.faqContainer}>
        <h4 className={classes.faqTitle}>{t('billing.emptyPage.faq.title')}</h4>
        <FAQItem
          question={t(`billing.emptyPage.faq.items.validity.question`)}
          answer={t(`billing.emptyPage.faq.items.validity.answer`)}
        />
        <FAQItem
          question={t(`billing.emptyPage.faq.items.credits.question`)}
          answer={t(`billing.emptyPage.faq.items.credits.answer`)}
        />
        <FAQItem
          question={t(`billing.emptyPage.faq.items.creditPayment.question`)}
          answer={t(`billing.emptyPage.faq.items.creditPayment.answer`)}
        />
        <FAQItem
          question={t(`billing.emptyPage.faq.items.subscription.question`)}
          answer={t(`billing.emptyPage.faq.items.subscription.answer`)}
        />
      </div>
    </div>
  );
};
